import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/shared/api/http-client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import type { PendingVenue } from "./types";
import { useRejectVenue } from "./use-reject-venue";

const MIN_REASON_LENGTH = 10;

interface RejectVenueDialogProps {
  readonly venue: PendingVenue | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function RejectVenueDialog({
  venue,
  open,
  onOpenChange,
  onSuccess,
}: RejectVenueDialogProps) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const reject = useRejectVenue();

  // Reset form state when venue changes or dialog reopens
  useEffect(() => {
    if (open) {
      setNotes("");
      setError(null);
    }
  }, [venue?.id, open]);

  if (!venue) return null;

  const trimmed = notes.trim();
  const isValid = trimmed.length >= MIN_REASON_LENGTH;

  const handleReject = () => {
    if (!isValid) return;
    setError(null);
    reject.mutate(
      { venueId: venue.id, input: { notes: trimmed } },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess();
        },
        onError: (err) => {
          if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
            // TODO(API-0.3): same MFA branch as ApproveVenueDialog
            setError(t("venues.errors.forbidden"));
          } else {
            setError(t("venues.reject.error"));
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reject-venue-dialog">
        <DialogHeader>
          <DialogTitle>{t("venues.reject.title")}</DialogTitle>
          <DialogDescription>
            {t("venues.reject.description", { name: venue.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-4">
          <label htmlFor="reject-reason" className="font-sans text-sm font-medium text-foreground">
            {t("venues.reject.reasonLabel")}
          </label>
          <Textarea
            id="reject-reason"
            data-testid="reject-reason-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("venues.reject.reasonPlaceholder")}
            rows={4}
          />
          <p className="font-sans text-xs font-normal text-muted-foreground">
            {t("venues.reject.reasonHint", { min: MIN_REASON_LENGTH })}
          </p>

          {error && (
            <p
              data-testid="reject-error"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={reject.isPending}>
            {t("venues.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-reject-button"
            variant="destructive"
            onClick={handleReject}
            disabled={!isValid || reject.isPending}
          >
            {reject.isPending ? t("venues.reject.rejecting") : t("venues.actions.reject")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
